/**
 * The lead detail drawer — why this lead scored what it did.
 *
 * Visual Reference: `design/screens/lead-detail.html`, 560px right-anchored,
 * states default · loading · error · overcap · override.
 *
 * PRD §7.4: "**Lead detail** | Five-section drawer. **Must be deep-linkable** —
 * §3.10's signed one-click override links from notifications target it
 * directly". So it is a ROUTE (`/leads/:lid`) and not a piece of list state: a
 * link in an email has to land here without going through the list first.
 *
 * ONE THING IT DELIBERATELY DOES NOT RENDER, DECISION-016:
 *   · **The call record and the voice-agent button** — M5. DECISION-024 is
 *     explicit that `Call {first name}` at M1 "is **not** the voice agent".
 *
 * **Extracted facts arrived at M2** and this comment used to say they had not.
 * Story 21.2 renders §3.9 section 2 — label, value and confidence — from the
 * R2 `extractions` slot, ABSENT rather than empty when nothing was read, which
 * is the free-tier drawer and the M1 drawer unchanged (§1.4 Tier 2 claim 2).
 *
 * AND ONE PLACE IT DEPARTS FROM THE MOCKUP ON PURPOSE. The export draws the
 * evidence glyphs as ✓ ✓ ~ − ? against 25/25, 20/20, 18/20, 15/25 and 0/10.
 * DECISION-026 fixes the thresholds at ≥ 0.85 → met, ≥ 0.4 → partial, > 0 →
 * weak — which makes 18/20 (0.9) `met` and 15/25 (0.6) `partial`, not `~` and
 * `−`. The decision answered Q-005 on 2026-08-23 and the export predates it, so
 * the function is law and the drawn glyphs are illustration. The API computes
 * them with `packages/schema`'s own `evidenceStrength`, which is the same
 * function the calibration probe calls, so the drawer and the probe cannot
 * disagree.
 *
 * Story 8.4.
 */
import { useCallback, useEffect, useId, useState } from 'react'
import { apiGet, apiPost, ApiFailure } from '../lib/api'
import { relative } from '../lib/relative'
import { useShell } from '../shell/workspace'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { Pill } from '../ui/Pill'
import { Select } from '../ui/Select'
import { Skeleton } from '../ui/Skeleton'
import { Leads } from './Leads'
import { ReviewQueue } from './ReviewQueue'

type Strength = 'met' | 'partial' | 'weak' | 'absent'

type Evidence = {
  criterionId: string
  label: string
  weight: number
  pointsAwarded: number
  pointsMax: number
  strength: Strength
  answer: string | null
}

type DrawerAnswer = { blockId: string; question: string; display: string | null }

/** PRD §3.9 section 2. `confidence` arrives 0–100; the mockup writes `92%`. */
/** Story 21.4. Two keys and no third — there is no slot for a band. */
type LeadAnalysis = { rationale: string | null; coherenceFlags: string[] }

type ExtractedFact = {
  fieldKey: string
  label: string
  value: string | null
  confidence: number | null
  /** Story 21.3. The respondent's own words, sliced out of the stored answer. */
  sourceSpan: string | null
  sourceQuestion: string | null
}

type Attribution = {
  country: string | null
  utm?: { source?: string; medium?: string; campaign?: string }
  device: string | null
  browser: string | null
  time_on_funnel_ms: number | null
  step_timings?: Record<string, number>
} | null

type Detail = {
  lead: {
    id: string
    contactName?: string | null
    contactCompany?: string | null
    /** Absent for a Viewer without the grant — `redactLead` removed it. */
    contactPhone?: string | null
    score?: number
    verdictSentence?: string | null
    verdictReason?: string | null
    status: string
    createdAt: number
    attribution?: Attribution
  }
  bandLabel: string | null
  tone: 'hot' | 'review' | 'nurture' | null
  /** Story 22.4 — the funnel's own ladder, lowest first, for the override. */
  bands: { id: string; label: string; tone: 'hot' | 'review' | 'nurture'; sentence: string }[]
  thresholdSentence: string | null
  evidence: Evidence[]
  answers: DrawerAnswer[]
  extractedFacts: ExtractedFact[]
  analysis: LeadAnalysis | null
  scoreTotal: { awarded: number; max: number }
  activity: { id: string; action: string; summary: string; createdAt: number }[]
}

/** DECISION-026's four, as the mockup draws them. */
const GLYPH: Record<Strength, string> = { met: '✓', partial: '~', weak: '−', absent: '?' }

const GLYPH_TONE: Record<Strength, string> = {
  met: 'bg-mdx-hot-tint text-mdx-hot-ink',
  partial: 'bg-mdx-review-tint text-mdx-review-ink',
  weak: 'bg-mdx-nurture-tint text-mdx-slate',
  absent: 'bg-mdx-ground text-mdx-slate',
}

const PILL_TONE = { hot: 'hot', review: 'review', nurture: 'neutral' } as const

/** The export's line, beside the control and again under the dialog's actions. */
const KEEP_ORIGINAL = 'We keep the original score alongside your correction.'

/**
 * DECISION-134's five, as `lead-detail.html` draws them — Story 22.4 replaced
 * Story 8.5's. Mirrored from the API's `OVERRIDE_REASONS` and not imported: `app`
 * may not import from `api` (ARCH §2.3's one-way dependency), and the server
 * refuses anything outside its own vocabulary, so a drift here is a 400 the
 * operator can read rather than a bad write.
 */
const REASONS: [string, string][] = [
  ['rubric_overweighted', 'The rubric weighted something too heavily'],
  ['rubric_missed', 'The rubric missed something in their answers'],
  ['knows_company', 'I know this company'],
  ['fit_not_asked', "Wrong fit, for reasons the funnel doesn't ask about"],
  ['something_else', 'Something else'],
]

/** The escape hatch, which reveals a required note (DECISION-134). */
const NOTE_REQUIRED_FOR = 'something_else'

/**
 * "Leadgible put Sarah Chen in Hot at 78. Tell us what they should have been and
 * why…" — the export writes "she", and a respondent's pronouns are not something
 * the product knows.
 */
function overrideIntro(detail: Detail): string {
  const who = detail.lead.contactName?.trim() || 'this lead'
  const where = detail.bandLabel ? ` in ${detail.bandLabel}` : ''
  const at = typeof detail.lead.score === 'number' ? ` at ${detail.lead.score}` : ''
  return `Leadgible put ${who}${where}${at}. Tell us what they should have been and why — both are needed, because this becomes ground truth for calibration.`
}

const initials = (name: string | null | undefined): string =>
  name
    ? (name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '·')
    : '·'

/** "3 min 12 sec", the mockup's own phrasing. */
function duration(ms: number | null | undefined): string | null {
  if (!ms || ms <= 0) return null
  const total = Math.round(ms / 1000)
  const min = Math.floor(total / 60)
  const sec = total % 60
  return min > 0 ? `${min} min ${sec} sec` : `${sec} sec`
}

export function LeadDrawer({ leadId }: { leadId: string }) {
  const { workspace } = useShell()
  const workspaceId = workspace?.id ?? null

  /**
   * Where a notification's one-click link lands — Story 9.3.
   *
   * `GET /l/{lid}` verified the signature and redirected here, so by the time
   * the drawer renders the question is only what to open. Read ONCE at mount,
   * not on every render: a person who presses "Change this decision" and then
   * closes it should not have it reopened by a query string that is still in
   * the address bar.
   */
  const [arrival] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return {
      override: params.get('override') === '1',
      expired: params.get('link') === 'expired',
      // Story 22.4: opened from the review queue, so closing goes back there.
      fromQueue: params.get('from') === 'queue',
    }
  })

  const [detail, setDetail] = useState<Detail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [calling, setCalling] = useState(false)
  const [overriding, setOverriding] = useState(arrival.override)
  const [band, setBand] = useState('')
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [overrideError, setOverrideError] = useState<string | null>(null)
  const bandHeading = useId()
  const noteId = useId()

  /**
   * The reveal, Story 21.3 — two pieces of state rather than one, on purpose.
   *
   * `grazed` is hover OR keyboard focus, and it comes and goes with the
   * pointer or the focus ring. `pinned` is a click, and it stays. Without the
   * second there is no reveal at all on a touch screen, where nothing is ever
   * hovered — and the M2 exit criterion is a claim the product makes to
   * everyone who reads it, not to everyone with a mouse.
   */
  const [pinned, setPinned] = useState<string | null>(null)
  const [grazed, setGrazed] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!workspaceId) return
    setError(null)
    setDetail(null)
    try {
      setDetail(await apiGet<Detail>(`/v1/w/${workspaceId}/leads/${leadId}`))
    } catch (e) {
      setError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't open this lead. It is still captured and still scored — try again.",
      )
    }
  }, [workspaceId, leadId])

  useEffect(() => {
    void load()
  }, [load])

  const close = () => window.location.assign(arrival.fromQueue ? '/review-queue' : '/leads')

  /**
   * DECISION-024: "at M1 the action is a `tel:` link **and** a logged call…
   * It is the drawer's instance of DECISION-017's 'Mark as called', not a
   * second path beside it."
   *
   * So it calls the SAME route the selection bar calls, with one id. Not a new
   * endpoint, not a second status write — literally not a second path. The
   * `tel:` href is left to the anchor so the phone still rings if the write
   * fails, which is the order that matters: the call is the point.
   */
  const logCall = async () => {
    if (!workspaceId || calling) return
    setCalling(true)
    try {
      await apiPost(`/v1/w/${workspaceId}/leads/bulk`, {
        action: 'mark_contacted',
        ids: [leadId],
      })
      await load()
    } catch {
      // The phone is already dialling. A failed status write is worth saying,
      // and not worth blocking the call over.
      setError('The call started, but we could not mark this lead as contacted.')
    } finally {
      setCalling(false)
    }
  }

  /** A band and a reason, and a note when the reason is the escape hatch. */
  const noteRequired = reason === NOTE_REQUIRED_FOR
  const ready = Boolean(band && reason && (!noteRequired || note.trim()))

  /**
   * STABLE, because `Modal` re-runs its focus effect whenever `onClose` changes —
   * a fresh function on every keystroke would pull the cursor out of the note.
   */
  const cancelOverride = useCallback(() => {
    setOverriding(false)
    setBand('')
    setReason('')
    setNote('')
    setOverrideError(null)
  }, [])

  const saveOverride = async () => {
    if (!workspaceId || !ready || saving) return
    setSaving(true)
    setOverrideError(null)
    try {
      await apiPost(`/v1/w/${workspaceId}/leads/${leadId}/override`, {
        band,
        reasonCode: reason,
        // Only "Something else" draws a note, so only it sends one.
        ...(noteRequired ? { reasonNote: note.trim() } : {}),
      })
      cancelOverride()
      await load()
    } catch (e) {
      // Said INSIDE the dialog, beside the choice that failed. The drawer's own
      // error state means the lead could not be opened, which is not what
      // happened — Story 8.5 put it there, and it replaced the whole drawer.
      setOverrideError(
        e instanceof ApiFailure ? e.message : "That correction couldn't be saved. Try again.",
      )
    } finally {
      setSaving(false)
    }
  }

  const firstName = detail?.lead.contactName?.trim().split(/\s+/)[0] ?? null

  return (
    <div className="fixed inset-0 z-20 flex justify-end">
      <button
        type="button"
        aria-label="Close"
        onClick={close}
        className="flex-1 cursor-default border-0 bg-mdx-backdrop p-0"
      />

      <section
        aria-label="Lead detail"
        className="flex w-mdx-drawer max-w-full flex-col bg-mdx-white shadow-mdx-drawer"
      >
        <header className="flex flex-none items-center gap-mdx-12 border-b border-mdx-line px-mdx-20 py-mdx-16">
          <span
            aria-hidden="true"
            className="flex size-mdx-avatar-lg flex-none items-center justify-center rounded-mdx-pill bg-mdx-brand text-mdx-13 font-mdx-bold text-mdx-white"
          >
            {initials(detail?.lead.contactName)}
          </span>
          <span className="flex min-w-0 flex-1 flex-col leading-mdx-tight">
            <span className="truncate text-mdx-17 font-mdx-bold tracking-mdx-h3 text-mdx-ink">
              {detail?.lead.contactName ?? 'Lead'}
            </span>
            <span className="truncate text-mdx-14 text-mdx-slate">
              {[detail?.lead.contactCompany, detail ? relative(detail.lead.createdAt) : null]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </span>
          {detail?.bandLabel && detail.tone ? (
            <Pill tone={PILL_TONE[detail.tone]} dot>
              {detail.bandLabel}
            </Pill>
          ) : null}
          <button
            type="button"
            aria-label="Close"
            onClick={close}
            className="flex size-mdx-icon-button flex-none cursor-pointer items-center justify-center rounded-mdx-control border border-mdx-line bg-mdx-white text-mdx-14 text-mdx-slate transition-colors ease-mdx-swift hover:bg-mdx-ground"
          >
            ✕
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-mdx-24 overflow-y-auto px-mdx-20 pb-mdx-32 pt-mdx-24">
          {error ? (
            <div className="flex flex-col items-start gap-mdx-12">
              <h2 className="m-0 text-mdx-20 font-mdx-bold tracking-mdx-h3 text-mdx-ink">
                We couldn&rsquo;t open this lead
              </h2>
              <p className="m-0 max-w-mdx-measure text-mdx-15 text-mdx-slate">{error}</p>
              <Button onClick={() => void load()}>Try again</Button>
            </div>
          ) : detail === null ? (
            <div className="flex flex-col gap-mdx-16" aria-busy="true">
              <Skeleton className="h-mdx-32 w-full rounded-mdx-sm" />
              <Skeleton className="h-mdx-18 w-full rounded-mdx-sm" />
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-mdx-40 w-full rounded-mdx-control" />
              ))}
            </div>
          ) : (
            <>
              {/* 1 — the Verdict. PRD §1.9: a sentence before a number. */}
              <section className="flex flex-col gap-mdx-12">
                <h2 className="m-0 text-balance text-mdx-verdict font-mdx-bold leading-mdx-tight tracking-mdx-h1 text-mdx-ink">
                  {detail.lead.verdictSentence ?? 'Not scored.'}
                </h2>
                {detail.lead.verdictReason ? (
                  <p className="m-0 max-w-mdx-measure text-mdx-18 leading-mdx-body">
                    {detail.lead.verdictReason}
                  </p>
                ) : null}
                <a
                  href="#answers"
                  className="self-start border-b border-mdx-brand-tint text-mdx-15 font-mdx-semibold text-mdx-brand-deep no-underline"
                >
                  See {firstName ? `${firstName}’s` : 'the'} full answers
                </a>
              </section>

              {/* 2 — evidence. The glyph is DECISION-026's, computed server-side. */}
              <section className="flex flex-col gap-mdx-2">
                {detail.evidence.map((row) => (
                  <div
                    key={row.criterionId}
                    className="flex flex-wrap items-center gap-x-mdx-12 gap-y-mdx-4 rounded-mdx-control px-mdx-12 py-mdx-10 odd:bg-mdx-ground"
                  >
                    <span
                      aria-label={row.strength}
                      className={[
                        'flex size-mdx-evidence-icon flex-none items-center justify-center',
                        'rounded-mdx-control-sm text-mdx-14 font-mdx-bold',
                        GLYPH_TONE[row.strength],
                      ].join(' ')}
                    >
                      {GLYPH[row.strength]}
                    </span>
                    <span className="w-mdx-row-identity-sm shrink-0 font-mdx-semibold text-mdx-ink">
                      {row.label}
                    </span>
                    <span className="min-w-0 grow basis-mdx-row-control text-mdx-15 text-mdx-slate">
                      {row.answer ?? 'Didn’t mention it'}
                    </span>
                    <span className="flex-none text-mdx-13 text-mdx-slate tabular-nums">
                      {row.pointsAwarded} of {row.pointsMax}
                    </span>
                  </div>
                ))}
              </section>

              {/* 3 — the score, and the line it is measured against. */}
              <section className="flex flex-wrap items-center gap-mdx-16 rounded-mdx-card border border-mdx-line bg-mdx-ground px-mdx-16 py-mdx-16">
                <span className="text-mdx-32 font-mdx-bold text-mdx-ink tabular-nums">
                  {detail.lead.score ?? '—'}
                </span>
                {detail.thresholdSentence ? (
                  <span className="min-w-0 grow basis-mdx-row-control text-mdx-15 text-mdx-slate">
                    {detail.thresholdSentence}
                  </span>
                ) : null}
                {/*
                  DECISION-024. A `tel:` link AND a logged call — the anchor
                  dials, the click logs.

                  Rendered only when there IS a number, which is also the
                  Viewer case: `redactLead` removes `contactPhone` without the
                  `pii_unredacted` grant, so a Viewer sees no Call button rather
                  than one that dials nothing. A "Call Sarah" that cannot call
                  is worse than none.

                  Stripped to digits and `+` for the href: an operator may have
                  typed "+44 7700 900123" and `tel:` wants no spaces.
                */}
                {detail.lead.contactPhone ? (
                  <a
                    href={`tel:${detail.lead.contactPhone.replace(/[^\d+]/g, '')}`}
                    onClick={() => void logCall()}
                    className="flex-none cursor-pointer rounded-mdx-control border-0 bg-mdx-brand px-mdx-24 py-mdx-12 text-mdx-15 font-mdx-semibold text-mdx-white no-underline"
                  >
                    Call {firstName ?? 'them'}
                  </a>
                ) : null}
              </section>

              {/* 4 — raw answers. */}
              <section id="answers" className="flex flex-col gap-mdx-8">
                <SectionHead title="Raw answers" meta={`${detail.answers.length} questions`} />
                {detail.answers.map((a) => (
                  <div key={a.blockId} className="flex flex-col gap-mdx-2 py-mdx-6">
                    <span className="text-mdx-14 text-mdx-slate">{a.question}</span>
                    <span className="text-mdx-15 text-mdx-ink">
                      {a.display ?? <span className="text-mdx-slate">Redacted</span>}
                    </span>
                  </div>
                ))}
              </section>

              {/*
                5 — extracted facts. PRD §3.9 section 2, between Raw answers
                and Score breakdown, which is the order the section list gives
                and the order `lead-detail.html` draws.

                THE SECTION IS ABSENT, NOT EMPTY. Story 21.2: "a heading over
                nothing reads as broken." The mockup draws the pair — Sarah
                Chen, who wrote nothing, has no heading for a section with no
                rows; Marcus Webb has four facts. An operator on the free tier,
                or with no extraction fields defined, sees the M1 drawer exactly
                as it was.

                THE REVEAL IS §1.4 TIER 2 CLAIM 1 — Story 21.3, the M2 exit
                criterion. A fact with a located source sentence is a BUTTON:
                the mockup draws one, and a button is focusable, Enter- and
                Space-activated and announced without anything being added for
                it. Hover and focus both reveal; a click PINS, so the sentence
                survives the pointer leaving and can be read on a touch screen
                where there is no hover at all.

                A fact whose span did not verify, or cannot be located in the
                answers now, is a plain div with no affordance — Story 21.3's
                third criterion. No reveal beats an invented one.
              */}
              {detail.extractedFacts.length > 0 ? (
                <section className="flex flex-col gap-mdx-8">
                  <SectionHead
                    title="Extracted facts"
                    meta={`${detail.extractedFacts.length} ${
                      detail.extractedFacts.length === 1 ? 'fact' : 'facts'
                    }`}
                  />
                  <p className="m-0 text-mdx-13 text-mdx-slate">
                    Hover or focus a fact to read the sentence it came from.
                  </p>
                  {detail.extractedFacts.map((fact) => {
                    const open = pinned === fact.fieldKey || grazed === fact.fieldKey
                    const revealId = `fact-span-${fact.fieldKey}`
                    const row = (
                      <>
                        <span className="w-mdx-row-identity-sm shrink-0 text-mdx-15 text-mdx-slate">
                          {fact.label}
                        </span>
                        <span className="min-w-0 grow basis-mdx-row-control-sm text-mdx-15 font-mdx-semibold text-mdx-ink">
                          {fact.value ?? (
                            <span className="font-mdx-regular text-mdx-slate">Redacted</span>
                          )}
                        </span>
                        {fact.confidence !== null ? (
                          <span className="flex-none whitespace-nowrap text-mdx-14 text-mdx-slate tabular-nums">
                            {fact.confidence}%
                          </span>
                        ) : null}
                      </>
                    )
                    const shape =
                      'flex w-full flex-wrap items-center gap-x-mdx-16 gap-y-mdx-8 rounded-mdx-control border border-mdx-line px-mdx-16 py-mdx-12 text-left'

                    return (
                      <div key={fact.fieldKey} className="flex flex-col gap-mdx-6">
                        {fact.sourceSpan ? (
                          <button
                            type="button"
                            aria-expanded={open}
                            aria-controls={revealId}
                            onClick={() =>
                              setPinned((p) => (p === fact.fieldKey ? null : fact.fieldKey))
                            }
                            onMouseEnter={() => setGrazed(fact.fieldKey)}
                            onMouseLeave={() => setGrazed((g) => (g === fact.fieldKey ? null : g))}
                            onFocus={() => setGrazed(fact.fieldKey)}
                            onBlur={() => setGrazed((g) => (g === fact.fieldKey ? null : g))}
                            className={`${shape} cursor-pointer bg-mdx-white transition-colors ease-mdx-swift hover:bg-mdx-ground focus-visible:border-mdx-brand`}
                          >
                            {row}
                          </button>
                        ) : (
                          <div className={shape}>{row}</div>
                        )}
                        {fact.sourceSpan && open ? (
                          <div
                            id={revealId}
                            className="flex flex-col gap-mdx-4 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12"
                          >
                            {/*
                              The respondent's own characters, sliced out of the
                              stored answer by `resolveSourceSpan` — not the
                              model's copy of them. Quoted, because it is one.
                            */}
                            <span className="text-mdx-15 leading-mdx-body text-mdx-ink">
                              &ldquo;{fact.sourceSpan}&rdquo;
                            </span>
                            {fact.sourceQuestion ? (
                              <span className="text-mdx-13 text-mdx-slate">
                                from <em>{fact.sourceQuestion}</em>
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    )
                  })}
                </section>
              ) : null}

              {/* 6 — the arithmetic, shown. "Humans see why." */}
              <section className="flex flex-col gap-mdx-8">
                <SectionHead
                  title="Score breakdown"
                  meta={`${detail.scoreTotal.awarded} of ${detail.scoreTotal.max}`}
                />
                <table className="w-full border-collapse text-mdx-14">
                  <thead>
                    <tr className="text-left text-mdx-13 text-mdx-slate">
                      <th className="py-mdx-6 font-mdx-semibold">Criterion</th>
                      <th className="py-mdx-6 font-mdx-semibold">Weight</th>
                      <th className="py-mdx-6 text-right font-mdx-semibold">Adds</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.evidence.map((row) => (
                      <tr key={row.criterionId} className="border-t border-mdx-line-soft">
                        <td className="py-mdx-8 text-mdx-ink">{row.label}</td>
                        <td className="py-mdx-8 text-mdx-slate tabular-nums">{row.weight}%</td>
                        <td className="py-mdx-8 text-right text-mdx-ink tabular-nums">
                          {row.pointsAwarded} of {row.pointsMax}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-mdx-line font-mdx-semibold">
                      <td className="py-mdx-8 text-mdx-ink">Total</td>
                      <td />
                      <td className="py-mdx-8 text-right text-mdx-ink tabular-nums">
                        {detail.scoreTotal.awarded} of {detail.scoreTotal.max}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>

              {/*
                7 — what the MODEL said. PRD §3.9 section 4 and §3.8, Story 21.4.

                ONE CARD FOR BOTH, which is how `lead-detail.html` draws it and
                is the answer to the criterion. Every section above is either a
                bordered disclosure or a plain stack of arithmetic; this is a
                bordered card with a 15px title rather than an eyebrow, sitting
                after the Score breakdown and holding the two things the model
                produced. An observation is not mistaken for a deduction
                because the two do not share a container — the breakdown is
                arithmetic an operator can check, this is the model talking.

                THE RATIONALE IS AN ADDITION AND NEVER A REPLACEMENT. RULE-032:
                "the top-contributing criteria ship at M1, not M2 … the M2 AI
                rationale is an enhancement to an email that already works,
                never the thing that makes it work." Story 19.6 wired the
                notification exactly this way, so the drawer and the email say
                the same thing about the same lead, and the M1 verdict
                paragraph at the top of this drawer is untouched either way.
              */}
              {detail.analysis &&
              (detail.analysis.rationale || detail.analysis.coherenceFlags.length > 0) ? (
                <section className="flex flex-col gap-mdx-14 rounded-mdx-control border border-mdx-line px-mdx-16 py-mdx-14">
                  {detail.analysis.rationale ? (
                    <>
                      <span className="text-mdx-15 font-mdx-semibold text-mdx-ink">
                        {/* The mockup's own sentence. Without a score there is
                            no number to name, so the clause goes rather than
                            being filled with an invented one. */}
                        {detail.lead.score !== undefined && detail.lead.score !== null
                          ? `Why this came out at ${detail.lead.score}`
                          : 'Why this came out'}
                      </span>
                      <p className="m-0 max-w-mdx-measure text-mdx-15 leading-mdx-body text-mdx-ink">
                        {detail.analysis.rationale}
                      </p>
                    </>
                  ) : null}

                  {detail.analysis.coherenceFlags.length > 0 ? (
                    <div className="flex flex-col gap-mdx-8">
                      <SectionHead
                        title="Coherence flags"
                        meta={`${detail.analysis.coherenceFlags.length} ${
                          detail.analysis.coherenceFlags.length === 1 ? 'flag' : 'flags'
                        }`}
                      />
                      {detail.analysis.coherenceFlags.map((note) => (
                        <div
                          key={note}
                          className="flex items-start gap-mdx-16 rounded-mdx-control bg-mdx-review-tint px-mdx-16 py-mdx-12"
                        >
                          <span
                            aria-hidden="true"
                            className="flex size-mdx-evidence-icon flex-none items-center justify-center rounded-mdx-control-sm bg-mdx-review text-mdx-14 font-mdx-bold text-mdx-white"
                          >
                            !
                          </span>
                          <span className="min-w-0 flex-1 text-mdx-15 leading-mdx-body text-mdx-ink">
                            {note}
                          </span>
                        </div>
                      ))}
                      {/*
                        The one piece of copy on this screen that states
                        RULE-026 to the PERSON reading it rather than to the
                        codebase. The mockup's own words.
                      */}
                      <p className="m-0 max-w-mdx-measure text-mdx-13 text-mdx-slate">
                        Flags never change a score or a band. They tell you what to ask about.
                      </p>
                    </div>
                  ) : null}
                </section>
              ) : null}

              {/* 8 — attribution. */}
              {detail.lead.attribution ? (
                <section className="flex flex-col gap-mdx-8">
                  <SectionHead title="Attribution" />
                  <Fact label="Country" value={detail.lead.attribution.country} />
                  <Fact
                    label="UTM source"
                    value={[
                      detail.lead.attribution.utm?.source,
                      detail.lead.attribution.utm?.medium,
                      detail.lead.attribution.utm?.campaign,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                  <Fact
                    label="Device"
                    value={[detail.lead.attribution.device, detail.lead.attribution.browser]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                  <Fact
                    label="Time on funnel"
                    value={duration(detail.lead.attribution.time_on_funnel_ms)}
                  />
                  <Fact
                    label="Step timings"
                    value={Object.entries(detail.lead.attribution.step_timings ?? {})
                      .map(([step, ms]) => `${step} ${duration(ms) ?? '0 sec'}`)
                      .join(' · ')}
                  />
                </section>
              ) : null}

              {/* 9 — the activity log, oldest first: a story reads forwards. */}
              <section className="flex flex-col gap-mdx-8">
                <SectionHead title="Activity" meta={`${detail.activity.length} events`} />
                {detail.activity.length === 0 ? (
                  <span className="text-mdx-14 text-mdx-slate">Nothing recorded yet.</span>
                ) : (
                  detail.activity.map((e) => (
                    <div key={e.id} className="flex items-baseline gap-mdx-12 py-mdx-4">
                      <span className="min-w-0 flex-1 text-mdx-14 text-mdx-ink">{e.summary}</span>
                      <span className="flex-none text-mdx-13 text-mdx-slate tabular-nums">
                        {new Date(e.createdAt).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  ))
                )}
              </section>

              {/*
                Override — Story 8.5, redrawn by Story 22.4 to the M2 export: a
                trigger row here, and the choice itself in a dialog over the
                drawer (below), offering the funnel's OWN bands from the detail
                response rather than three ids typed into this file.
              */}
              <section className="flex flex-wrap items-center gap-mdx-12 border-t border-mdx-line-soft pt-mdx-20">
                {/*
                  A link that was ours and simply got old. The lead is open and
                  everything on it works — only the shortcut expired — so this
                  says that rather than reading as a refusal.
                */}
                {arrival.expired ? (
                  <p className="m-0 w-full max-w-mdx-measure text-mdx-13 text-mdx-slate">
                    That link from your email has expired, so the correction panel isn&rsquo;t open.
                    Nothing else has changed &mdash; you can still change this decision below.
                  </p>
                ) : null}
                <Button variant="secondary" onClick={() => setOverriding(true)}>
                  Change this decision
                </Button>
                <span className="min-w-0 grow basis-mdx-row-identity text-mdx-13 text-mdx-slate">
                  {KEEP_ORIGINAL}
                </span>
              </section>
            </>
          )}
        </div>
      </section>

      {/*
        The correction — `lead-detail.html`'s `override` state, Story 22.4. A
        band and a structured reason are both required before it saves, and
        "Something else" adds a required note (DECISION-134).
      */}
      {overriding && detail ? (
        <Modal
          title="Change this decision"
          subtitle={overrideIntro(detail)}
          onClose={cancelOverride}
          footnote={KEEP_ORIGINAL}
          actions={
            <>
              <Button variant="secondary" onClick={cancelOverride}>
                Cancel
              </Button>
              <Button
                onClick={() => void saveOverride()}
                disabled={!ready}
                loading={saving ? 'Saving the correction' : undefined}
              >
                Save the correction
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-mdx-8">
            <span id={bandHeading} className="text-mdx-14 font-mdx-semibold text-mdx-ink">
              Band
              <span aria-hidden="true" className="text-mdx-error">
                {' *'}
              </span>
            </span>
            {detail.bands.length > 0 ? (
              <div role="radiogroup" aria-labelledby={bandHeading} className="flex flex-col gap-mdx-8">
                {detail.bands.map((option) => (
                  <label
                    key={option.id}
                    className="flex cursor-pointer flex-wrap items-center gap-mdx-10 rounded-mdx-control border border-mdx-line px-mdx-14 py-mdx-10 text-mdx-15 transition-colors ease-mdx-swift hover:bg-mdx-ground"
                  >
                    <input
                      type="radio"
                      name="override-band"
                      value={option.id}
                      checked={band === option.id}
                      onChange={() => setBand(option.id)}
                      className="size-mdx-18 flex-none cursor-pointer accent-mdx-brand"
                    />
                    <Pill tone={PILL_TONE[option.tone]} dot>
                      {option.label}
                    </Pill>
                    <span className="text-mdx-13 text-mdx-slate">{option.sentence.replace(/\.$/, '')}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="m-0 text-mdx-13 text-mdx-slate">
                {"This funnel's bands couldn't be read, so this decision can't be changed here."}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-mdx-6">
            <span aria-hidden="true" className="text-mdx-14 font-mdx-semibold text-mdx-ink">
              Reason
              <span className="text-mdx-error">{' *'}</span>
            </span>
            <Select label="Reason" value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="">Choose a reason</option>
              {REASONS.map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </Select>
          </div>

          {/* COMPOSED: the export describes this field and draws none of it. */}
          {noteRequired ? (
            <div className="flex flex-col gap-mdx-6">
              <label htmlFor={noteId} className="text-mdx-14 font-mdx-semibold text-mdx-ink">
                Note
                <span aria-hidden="true" className="text-mdx-error">
                  {' *'}
                </span>
              </label>
              <textarea
                id={noteId}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={500}
                aria-required="true"
                aria-describedby={`${noteId}-helper`}
                className="min-h-mdx-textarea-min rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-14 py-mdx-10 text-mdx-15 text-mdx-ink outline-none transition-colors duration-mdx-swift ease-mdx-swift focus:border-mdx-brand focus:[box-shadow:var(--mdx-focus-ring)]"
              />
              <span id={`${noteId}-helper`} className="text-mdx-13 text-mdx-slate">
                {"What happened that the other four reasons don't cover?"}
              </span>
            </div>
          ) : null}

          {overrideError ? (
            <p role="alert" className="m-0 text-mdx-13 text-mdx-error">
              {overrideError}
            </p>
          ) : null}
        </Modal>
      ) : null}
    </div>
  )
}

function SectionHead({ title, meta }: { title: string; meta?: string }) {
  return (
    <div className="flex items-center gap-mdx-12">
      <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
        {title}
      </span>
      <span className="h-px flex-1 bg-mdx-line-soft" />
      {meta ? <span className="text-mdx-13 text-mdx-slate tabular-nums">{meta}</span> : null}
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null
  return (
    <div className="flex flex-wrap items-baseline gap-x-mdx-12 gap-y-mdx-2 py-mdx-4">
      <span className="w-mdx-row-identity-sm shrink-0 text-mdx-14 text-mdx-slate">{label}</span>
      <span className="min-w-0 flex-1 text-mdx-14 text-mdx-ink">{value}</span>
    </div>
  )
}

/**
 * The route `/leads/:lid` renders.
 *
 * The list stays MOUNTED behind the drawer, which is what the export draws —
 * four leads visible down the left, the drawer over them — and what makes
 * closing it feel like closing a drawer rather than navigating back to a page
 * that has to load again.
 *
 * The lead id comes off the pathname rather than from a router param because
 * `routes.tsx` is a table of predicates, not a router (its own header says so):
 * "no route params until the leads drawer" was written there in Story 6.2, and
 * this is that drawer.
 *
 * Opened from the review queue (`?from=queue`, Story 22.4), the QUEUE stays
 * mounted behind it instead, and closing returns there: a Reviewer decides a
 * lead and is back at the next one, not on a list they did not come from.
 */
export function LeadDetailRoute() {
  const lid = window.location.pathname.replace(/^\/leads\//, '').replace(/\/$/, '')
  const fromQueue = new URLSearchParams(window.location.search).get('from') === 'queue'
  return (
    <>
      {fromQueue ? <ReviewQueue /> : <Leads />}
      <LeadDrawer leadId={lid} />
    </>
  )
}
